import React, { PropsWithChildren } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, TooltipProps, } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import * as MaterialUIIcons from '../../controls/MaterialUIIcons';
import { AcosIcon } from '../../controls/AcosIcon';
import { useTheme } from '@mui/styles';
import { useTranslation } from 'react-i18next';

export type CtxMenuItem<T> = {
    icon: React.ReactElement;
    label: string;
    onClick?: ((entity: T) => void) | (() => void);
    testId?: string;
    hidden?: boolean | ((entity: T) => boolean);
    disabled?: boolean | ((entity: T) => boolean);
    tooltip?: Omit<TooltipProps, 'children'>;
};

const useStyles = makeStyles((theme) =>
    createStyles({
        paper: {
            border: `1px solid ${theme.palette.divider}`,
            outline: 0,
        },
        contextMenu: {
            pointerEvents: 'none',
        },
        contextMenuPaper: {
            pointerEvents: 'auto',
        },
    })
);

type Props<T> = {
    open: boolean;
    isTreeView: boolean;
    anchorEl?: Element;
    entity: T | undefined;
    X?: number;
    Y?: number;
    testId?: string;
    canEdit: boolean;
    canCreate: boolean;
    canCopy: boolean;
    canDelete: boolean;
    onClose(): void;
    openEdit(entity: T): void;
    openView(entity: T): void;
    createChild?(entity: T): void;
    openCopy(entity: T): void;
    openLog(entity: T): void;
    openDelete(entity: T): void;
    expandChildrenNodes?: (entity: T) => void;
    expandAllNodes?: () => void;
    hideAllCallBack?: () => void;
    expandChildrenLessNodes?: (val: T) => void;
    configureCtxMenuItems?: (items: CtxMenuItem<T>[]) => CtxMenuItem<T>[];
};

export default function AdminDataGridContextMenu<T>(
    props: PropsWithChildren<Props<T>>
) {
    const classes = useStyles();
    const { t } = useTranslation();

    function initMenuItems(): CtxMenuItem<T>[] {
        let items: CtxMenuItem<T>[] = getCtxMenuItems();
        if (props.isTreeView) {
            items = [...items, ...getTreeViewCtxMenuItems()];
        }
        if (props.configureCtxMenuItems) return props.configureCtxMenuItems(items);
        else return items;
    }

    function getCtxMenuItems(): CtxMenuItem<T>[] {
        return [
            {
                label: t('Rediger'),
                onClick: props.openEdit,
                testId: props.testId + '-rediger-btn',
                hidden: !props.canEdit,
                icon: <MaterialUIIcons.EditIcon fontSize="small" />,
            },
            {
                label: t('Vis'),
                onClick: props.openView,
                testId: props.testId + '-vis-btn',
                hidden: props.canEdit,
                icon: <MaterialUIIcons.InfoIcon fontSize="small" />,
            },
            {
                label: t('Kopier'),
                testId: props.testId + '-kopier-btn',
                onClick: props.openCopy,
                hidden: !props.canCreate || !props.canCopy,
                icon: <MaterialUIIcons.ContentCopyIcon fontSize="small" />,
            },
            {
                label: t('Vis logg'),
                testId: props.testId + '-visLogg-btn',
                onClick: props.openLog,
                hidden: false,
                icon: <MaterialUIIcons.HistoryIcon fontSize="small" />,
            },
            {
                label: t('Slett'),
                testId: props.testId + '-slett-btn',
                onClick: props.openDelete,
                hidden: !props.canDelete,
                icon: <MaterialUIIcons.DeleteIcon fontSize="small" />,
            },
        ];
    }

    function getTreeViewCtxMenuItems() {
        return [
            {
                label: t('Legg til ny'),
                onClick: props.createChild,
                testId: props.testId + '-leggTilNy-btn',
                hidden: !props.canCreate,
                icon: <MaterialUIIcons.AddIcon fontSize="small" />,
            },
            {
                label: t('Utvid denne og underliggende'),
                testId: props.testId + '-utvidDenneOgUnderliggende-btn',
                onClick: props.expandChildrenNodes,
                icon: <MaterialUIIcons.ExpandMoreIcon fontSize="small" />,
            },
            {
                label: t('Fold sammen denne og underliggende'),
                onClick: props.expandChildrenLessNodes,
                testId: props.testId + '-foldSammenDenneOgUnderliggende-btn',
                icon: <MaterialUIIcons.ExpandLessIcon fontSize="small" />,
            },
            // {
            //     label: t('Utvid alle'),
            //     onClick: props.expandAllNodes,
            //     testId: props.testId + '-utvidAlle-btn',
            //     icon: <AcosIcon color={theme.palette.grey[600]}>unfold_more</AcosIcon>,
            // },
            {
                label: t('Fold sammen alle'),
                onClick: props.hideAllCallBack,
                testId: props.testId + '-foldSammenAlle-btn',
                icon: <AcosIcon color={theme.palette.grey[600]}>unfold_less</AcosIcon>,
            },
        ];
    }

    const theme = useTheme();


    function openClick(action?: (entity: T) => void) {
        if (props.entity && action) {
            action(props.entity);
            props.onClose();
        }
    }


    let anchorPosition = undefined;
    if (typeof props.Y === 'number' && typeof props.X === 'number') {
        anchorPosition = {
            top: props.Y,
            left: props.X,
        };
    }
    const menuItems = initMenuItems();

    function filterHidden(hidden: CtxMenuItem<T>['hidden']) {
        if (typeof (hidden) === 'function') return props.entity && hidden(props.entity);
        else return hidden;
    }

    function renderMenuItem(item: CtxMenuItem<any>, index: number): React.ReactElement {
        return (
            <span key={index}>
                <MenuItem onClick={() => openClick(item.onClick)}>
                    <ListItemIcon test-id={item.testId}>
                        <IconButton color="primary" test-id={item.testId}> {item.icon} </IconButton>
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                </MenuItem>
            </span>
        );
    }

    function renderMenuItems() {
        return menuItems.filter(m => !filterHidden(m.hidden)).map((item, index) => {
            const menuItem = renderMenuItem(item, index);
            return (item.tooltip ? <Tooltip key={index} {...item.tooltip} children={menuItem} /> : menuItem);
        });
    }

    return (
        <>
            {props.X && props.Y ? (
                <Menu
                    anchorReference="anchorPosition"
                    anchorPosition={anchorPosition}
                    id="row-context-menu"
                    test-id="row-context-menu"
                   
                    PopoverClasses={{
                        root: classes.contextMenu,
                        paper: classes.contextMenuPaper,
                    }}
                    keepMounted
                    open={props.open}
                    onClose={props.onClose}
                >
                    {renderMenuItems()}
                </Menu>
            ) : (
                <Menu
                    className={classes.paper}
                    elevation={1}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    id="row-context-menu"
                    test-id="row-context-menu"
                    anchorEl={props.anchorEl}
                    keepMounted
                    open={props.open}
                    onClose={props.onClose}
                >
                    {renderMenuItems()}
                </Menu>
            )}
        </>
    );
}

